import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { HttpService } from './http.service';
import { IUser, User } from '@app/models';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly getUser: WritableSignal<IUser>;
  readonly init$: BehaviorSubject<boolean>;

  private http = inject(HttpService);
  private router = inject(Router);

  constructor() {
    this.getUser = signal(new User());
    this.init$ = new BehaviorSubject(false);

    localStorage.removeItem('user'); // TODO Remove any time.
    // Try to reload when the browser does.
    // This will work if the user is logged in.
    void this.reloadUser().finally(() => {
      this.init$.next(true);
    });
  }

  clearUser() {
    this.getUser.set(new User());
    localStorage.removeItem('user');
  }

  mergeUser(merged: Partial<IUser>) {
    const stored = Object.assign(this.getUser(), merged);
    this.storeUser(stored);
    return stored as IUser;
  }

  reloadUser() {
    return firstValueFrom(
      this.http.getMyProfile().pipe(
        tap((v) => {
          this.storeUser({ ...v, authuser: true });
        }),
      ),
    );
  }

  signIn(username: string, password: string) {
    return this.http.signIn(username, password).pipe(
      switchMap((v) => {
        this.http.storeTokens(v);
        return this.http.getMyProfile();
      }),
      tap((v) => {
        this.storeUser({ ...v, authuser: true });
      }),
      catchError(() => {
        this.http.clearTokens();
        this.clearUser();
        localStorage.clear();
        throw new Error('Unable to sign in');
      }),
    );
  }

  signOut() {
    return this.http.signOut().pipe(
      tap(() => {
        this.clearUser();
        this.http.clearTokens();
        localStorage.clear();
        void this.router.navigate(['/signin']);
      }),
      catchError(() => {
        this.clearUser();
        this.http.clearTokens();
        localStorage.clear();
        throw new Error('Unable to sign out');
      }),
    );
  }

  signUp(
    username: string,
    password: string,
    firstName: string,
    lastName: string,
  ) {
    return this.http.signUp(username, password, firstName, lastName).pipe(
      switchMap(() => {
        return this.signIn(username, password);
      }),
      catchError(() => {
        this.clearUser();
        this.http.clearTokens();
        localStorage.clear();
        throw new Error('Unable to sign up');
      }),
    );
  }

  storeUser(user: IUser) {
    // TODO Remove calls to this function.
    // localStorage.setItem('user', JSON.stringify(user));
    this.getUser.set(user);
  }
}
